// components/IOSInstallModal.jsx
import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useLanguage } from '../context/LanguageContext';
import { storageService } from '../utils/storageService';

const IOSInstallModal = () => {
  const [showModal, setShowModal] = useState(false);
  const [device, setDevice] = useState({ isIOS: false, isIPad: false });
  const [browser, setBrowser] = useState('safari');
  const [isStandalone, setIsStandalone] = useState(false);
  const { language } = useLanguage();

  // Detect iOS, distinguishing iPad — the share icon sits next to the address
  // bar at the top on iPad, but in the bottom toolbar on iPhone, so the
  // instructions must differ.
  const detectDevice = () => {
    const ua = window.navigator.userAgent.toLowerCase();
    const isIPad = /ipad/.test(ua) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isIPhone = /iphone|ipod/.test(ua);
    return { isIOS: isIPad || isIPhone, isIPad };
  };

  // All iOS browsers are WebKit underneath, but only Safari's share sheet
  // offers "Add to Home Screen" — detect wrappers so we can point users back
  // to Safari instead of giving them steps that won't work.
  const detectBrowser = () => {
    const ua = window.navigator.userAgent;
    if (/CriOS/i.test(ua)) return 'chrome';
    if (/FxiOS/i.test(ua)) return 'firefox';
    if (/EdgiOS/i.test(ua)) return 'edge';
    if (/OPiOS|OPT\//i.test(ua)) return 'opera';
    return 'safari';
  };

  const isRunningStandalone = () => {
    return window.navigator.standalone === true ||
      window.matchMedia('(display-mode: standalone)').matches;
  };

  const hasUserDismissed = () => {
    return storageService.getItem('iosInstallModalDismissed', false);
  };

  useEffect(() => {
    const detectedDevice = detectDevice();
    const standaloneMode = isRunningStandalone();

    setDevice(detectedDevice);
    setBrowser(detectBrowser());
    setIsStandalone(standaloneMode);

    if (detectedDevice.isIOS && !standaloneMode && !hasUserDismissed()) {
      const timer = setTimeout(() => setShowModal(true), 3000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleDontShowAgain = () => {
    storageService.setItem('iosInstallModalDismissed', true);
    setShowModal(false);
  };

  const handleClose = () => setShowModal(false);

  const texts = {
    en: {
      title: 'Install the HELEXPO App',
      subtitle: 'Add it to your Home Screen in 3 quick taps',
      shareTitle: 'Tap the Share icon',
      shareDetailIphone: "The square with the arrow, in Safari's toolbar at the bottom of the screen.",
      shareDetailIpad: 'The square with the arrow, next to the address bar at the top of the screen.',
      step2Title: "Choose 'Add to Home Screen'",
      step2Detail: 'Scroll down the share sheet until you see it, then tap it.',
      step3Title: "Tap 'Add' to finish",
      step3Detail: 'The HELEXPO icon appears on your Home Screen, ready to open like any app.',
      browserWarningTitle: 'Open this page in Safari',
      browserWarningDetail: (name) => `You're browsing in ${name}. On iOS, only Safari can add this app to your Home Screen.`,
      benefitsTitle: 'Why install it',
      benefit1: 'Faster loading',
      benefit2: 'Works offline',
      benefit3: 'Full-screen view',
      benefit4: 'One tap from Home',
      dontShowAgain: "Don't show again",
      close: 'Maybe later',
      footer: 'Works in Safari on iPhone and iPad',
      browserNames: { chrome: 'Chrome', firefox: 'Firefox', edge: 'Edge', opera: 'Opera', safari: 'Safari' },
    },
    gr: {
      title: 'Εγκατάσταση HELEXPO App',
      subtitle: 'Προσθήκη στην Αρχική Οθόνη σε 3 απλά βήματα',
      shareTitle: 'Πατήστε το εικονίδιο Κοινοποίησης',
      shareDetailIphone: 'Το τετράγωνο με το βέλος, στη γραμμή εργαλείων του Safari στο κάτω μέρος της οθόνης.',
      shareDetailIpad: 'Το τετράγωνο με το βέλος, δίπλα στη γραμμή διευθύνσεων στο πάνω μέρος της οθόνης.',
      step2Title: "Επιλέξτε 'Προσθήκη στην Αρχική Οθόνη'",
      step2Detail: 'Κυλήστε προς τα κάτω στο μενού κοινοποίησης μέχρι να τη βρείτε και πατήστε την.',
      step3Title: "Πατήστε 'Προσθήκη' για ολοκλήρωση",
      step3Detail: 'Το εικονίδιο HELEXPO εμφανίζεται στην Αρχική Οθόνη, έτοιμο να ανοίξει σαν κανονική εφαρμογή.',
      browserWarningTitle: 'Ανοίξτε τη σελίδα στο Safari',
      browserWarningDetail: (name) => `Χρησιμοποιείτε ${name}. Στο iOS, μόνο το Safari μπορεί να προσθέσει την εφαρμογή στην Αρχική Οθόνη.`,
      benefitsTitle: 'Γιατί να την εγκαταστήσετε',
      benefit1: 'Ταχύτερη φόρτωση',
      benefit2: 'Λειτουργεί χωρίς σύνδεση',
      benefit3: 'Πλήρης οθόνη',
      benefit4: 'Άμεση πρόσβαση',
      dontShowAgain: 'Να μην εμφανιστεί ξανά',
      close: 'Ίσως αργότερα',
      footer: 'Λειτουργεί στο Safari, σε iPhone και iPad',
      browserNames: { chrome: 'Chrome', firefox: 'Firefox', edge: 'Edge', opera: 'Opera', safari: 'Safari' },
    },
  };

  const t = texts[language] || texts.en;
  const isNonSafari = browser !== 'safari';

  if (!device.isIOS || isStandalone) {
    return null;
  }

  const steps = [
    {
      icon: 'bi-box-arrow-up',
      title: t.shareTitle,
      detail: device.isIPad ? t.shareDetailIpad : t.shareDetailIphone,
    },
    { icon: 'bi-plus-square', title: t.step2Title, detail: t.step2Detail },
    { icon: 'bi-check2-circle', title: t.step3Title, detail: t.step3Detail },
  ];

  const benefits = [
    { icon: 'bi-lightning-charge-fill', label: t.benefit1 },
    { icon: 'bi-wifi-off', label: t.benefit2 },
    { icon: 'bi-arrows-fullscreen', label: t.benefit3 },
    { icon: 'bi-house-door-fill', label: t.benefit4 },
  ];

  return (
    <Modal
      show={showModal}
      onHide={handleClose}
      centered
      className="pwa-install-modal"
      backdrop="static"
    >
      <Modal.Header className="pwa-install-header border-0">
        <div className="pwa-install-header-icon" aria-hidden="true">
          <i className="bi bi-apple" />
        </div>
        <div className="pwa-install-header-text">
          <Modal.Title className="pwa-install-title">{t.title}</Modal.Title>
          <div className="pwa-install-subtitle">{t.subtitle}</div>
        </div>
      </Modal.Header>

      <Modal.Body className="pwa-install-body">
        {isNonSafari && (
          <div className="pwa-install-browser-warning">
            <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />
            <div>
              <div className="pwa-install-warning-title">{t.browserWarningTitle}</div>
              <div className="pwa-install-warning-detail">
                {t.browserWarningDetail(t.browserNames[browser])}
              </div>
            </div>
          </div>
        )}

        <div className="pwa-install-steps">
          {steps.map((step, index) => (
            <div className="pwa-install-step" key={step.icon}>
              <div className="pwa-install-step-rail" aria-hidden="true">
                <span className="pwa-install-step-badge">
                  <i className={`bi ${step.icon}`} />
                </span>
                {index < steps.length - 1 && <span className="pwa-install-step-line" />}
              </div>
              <div className="pwa-install-step-content">
                <div className="pwa-install-step-title">{step.title}</div>
                <div className="pwa-install-step-detail">{step.detail}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="pwa-install-benefits">
          <div className="pwa-install-benefits-title">{t.benefitsTitle}</div>
          <div className="pwa-install-benefits-grid">
            {benefits.map((benefit) => (
              <div className="pwa-install-benefit" key={benefit.icon}>
                <i className={`bi ${benefit.icon}`} aria-hidden="true" />
                <span>{benefit.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="pwa-install-footer-hint">
          <i className="bi bi-info-circle" aria-hidden="true" />
          <span>{t.footer}</span>
        </div>

        <div className="pwa-install-actions">
          <Button className="pwa-install-btn-ghost" onClick={handleDontShowAgain}>
            {t.dontShowAgain}
          </Button>
          <Button className="pwa-install-btn-primary" onClick={handleClose}>
            {t.close}
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default IOSInstallModal;
